// my ticket 

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usePriorities } from '../../../hooks/usePriorities';
import { useStatuses } from '../../../hooks/useStatuses';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../../api/ticketService';
import AttachDuplicateModal from '../../../components/relations/AttachDuplicateModal';
import { getTicketStatusCode } from '../../../components/relations/CloseAsDuplicateModal';
import { useFeaturePermissions } from '../../../components/globalfeatures';
import { useTicketRelationsEnabled } from '../../../contexts/AppConfigContext';
import inquiryCategoryMasterService from '../../../api/inquiryCategoryMasterService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { computeSlaCountdown, isSlaClockFrozen } from '../../../utils/slaCountdown';
import { isManualMatrixEscalationEligible } from '../../../utils/manualMatrixEscalationEligibility';
import {
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveInquiryBreadcrumb,
} from '../../../utils/inquiryDisplay';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { minutesToHHMMSS, formatTimeWithSeconds } from '../../../components/globaldeclaration';
import { Alert } from '../../../components/ui/Alert';
import {
  Ticket,
  Search,
  Filter,
  Clock,
  User,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  MoreHorizontal,
  Eye,
  Edit,
  Archive,
  Star,
  Flag,
  Calendar,
  Tag,
  RefreshCw,
  X,
  Paperclip,
  Pen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Copy,
  ArrowUp,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/** D7 — duplicate ticket cannot be linked when in these status_code values. */
const DUPLICATE_LINK_BLOCKED_STATUS_CODES = new Set([
  'resolved',
  'closed',
  'supervisor_approved',
]);

/** List API returns status display label only; SLA pause keys off ticket_statuses.status_code. */
function enrichTicketStatusFromMaster(ticket, statuses) {
  if (!ticket || !Array.isArray(statuses) || statuses.length === 0) {
    return ticket;
  }
  const display =
    ticket.statusName
    || (typeof ticket.status === 'string' ? ticket.status : '')
    || '';
  const normalizedDisplay = display.trim().toLowerCase();
  if (!normalizedDisplay) {
    return ticket;
  }
  const master = statuses.find((s) => {
    const name = (s.statusName || s.name || '').trim().toLowerCase();
    const code = String(s.statusCode || s.code || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    return name === normalizedDisplay
      || code === normalizedDisplay.replace(/[\s-]+/g, '_');
  });
  if (!master) {
    return { ...ticket, statusName: display };
  }
  return {
    ...ticket,
    statusId: master.statusId ?? master.id ?? ticket.statusId,
    statusCode: master.statusCode ?? master.code ?? ticket.statusCode,
    statusName: master.statusName || master.name || display,
  };
}

const MyTickets = ({ onCreateTicket }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading state for search operations
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [showFilters, setShowFilters] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10); // Default page size (not used when fetchAll=true)
  const [paginationData, setPaginationData] = useState(null);
  const { data: priorities = [], isSuccess: prioritiesReady } = usePriorities();
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [inquiryCategories, setInquiryCategories] = useState([]);
  const { data: statusesRaw = [] } = useStatuses();
  const statuses = useMemo(
    () => statusesRaw.filter((status) => status.isActive !== false),
    [statusesRaw]
  );
  const ticketsForSla = useMemo(
    () => tickets.map((t) => enrichTicketStatusFromMaster(t, statuses)),
    [tickets, statuses]
  );
  // SLA timer state - Map of ticketId to { minutes, seconds }
  const [slaTimers, setSlaTimers] = useState(new Map());
  // Statistics state for accurate counts
  const [statistics, setStatistics] = useState({
    total: 0,
    open: 0,
    pending: 0,
    'in-progress': 0,
    resolved: 0,
    closed: 0,
    escalated: 0
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const loadTicketsRef = useRef(null);
  const loadStatisticsRef = useRef(null);
  const ticketsSlaFetchedAtRef = useRef(Date.now());
  const hasInitializedRef = useRef(false);
  const skipInitialFilterLoadRef = useRef(true);
  const skipInitialSearchLoadRef = useRef(true);
  const ticketRelationsEnabled = useTicketRelationsEnabled();
  const duplicateTicketLinkPerms = useFeaturePermissions('duplicate_ticket_link');
  const manualMatrixEscalationPerms = useFeaturePermissions('manual_inquiry_matrix_escalation');
  const canDuplicateLink = ticketRelationsEnabled && duplicateTicketLinkPerms.canView;
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  /** When true, row radio buttons are shown for duplicate linking. */
  const [duplicateSelectMode, setDuplicateSelectMode] = useState(false);
  const [showAttachDuplicateModal, setShowAttachDuplicateModal] = useState(false);

  /**
   * Live timer for the ticket's CURRENT SLA window, using the same countdown helper as the
   * Ticket Detail SLA widget. slaIsBreached / slaIsEscalated stay true after an auto-escalation
   * (they describe history), so they never decide remaining vs overdue here either.
   */
  const computeSlaTimerForTicket = (ticket, secondsSinceFetch) => {
    const hasRemaining = ticket.slaRemainingMinutes !== null
      && ticket.slaRemainingMinutes !== undefined;
    const hasOverdue = ticket.slaOverdueMinutes !== null
      || ticket.slaOverdueSeconds !== null;
    if (!hasRemaining && !hasOverdue) {
      return null;
    }

    const d1BusinessSla = ticket.slaUsesBusinessHours === true;
    const tickSeconds = isSlaClockFrozen(ticket, statuses) ? 0 : secondsSinceFetch;
    const countdown = computeSlaCountdown(
      {
        remainingMinutes: ticket.slaRemainingMinutes,
        remainingSeconds: ticket.slaRemainingSeconds,
        overdueMinutes: ticket.slaOverdueMinutes,
        overdueSeconds: ticket.slaOverdueSeconds,
      },
      tickSeconds,
      { slaUsesBusinessHours: d1BusinessSla }
    );

    return countdown.overdueMinutes !== null
      ? {
        minutes: countdown.overdueMinutes,
        seconds: countdown.overdueSeconds,
        isOverdue: true,
      }
      : {
        minutes: countdown.remainingMinutes,
        seconds: countdown.remainingSeconds,
        isOverdue: false,
      };
  };

  useEffect(() => {
    if (!prioritiesReady || hasInitializedRef.current) {
      return;
    }
    const initializeData = async () => {
      await loadTickets(false, priorities);
      hasInitializedRef.current = true;
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritiesReady, priorities]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [priorityRows, categoryResp] = await Promise.all([
          ticketService.getInquiryPriorityLevelsForCreate(),
          inquiryCategoryMasterService.getAll(true),
        ]);
        if (cancelled) return;
        setInquiryPriorityLevels(Array.isArray(priorityRows) ? priorityRows : []);
        const catData = categoryResp?.data ?? categoryResp;
        const catRows = Array.isArray(catData) ? catData : (catData?.content || []);
        setInquiryCategories(Array.isArray(catRows) ? catRows : []);
      } catch (e) {
        console.warn('Failed to load inquiry masters for My Tickets filters', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const response = await ticketService.getMyTicketsStatistics();
        if (cancelled) return;
        if (response?.data?.success && response?.data?.data) {
          const stats = response.data.data;
          setStatistics({
            total: stats.total || 0,
            open: stats.open || 0,
            pending: stats.pending || 0,
            'in-progress': stats['in-progress'] || 0,
            resolved: stats.resolved || 0,
            closed: stats.closed || 0,
            escalated: stats.escalated || 0
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load statistics:', error);
        }
      }
    };
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load statistics for accurate counts (once on mount; refresh via handleRefresh / realtime)
  const loadStatistics = async () => {
    try {
      const response = await ticketService.getMyTicketsStatistics();
      if (response?.data?.success && response?.data?.data) {
        const stats = response.data.data;
        setStatistics({
          total: stats.total || 0,
          open: stats.open || 0,
          pending: stats.pending || 0,
          'in-progress': stats['in-progress'] || 0,
          resolved: stats.resolved || 0,
          closed: stats.closed || 0,
          escalated: stats.escalated || 0
        });
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      // Keep default values (0) on error
    }
  };

  // Reload tickets when page changes (server-side pagination)
  // Skip this if searchTerm is set (search effect will handle it)
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (priorities.length > 0 && (!searchTerm || !searchTerm.trim())) {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    setSelectedTicketId(null);
  }, [currentPage]);

  // Server-side search: when searchTerm changes, fetch tickets with search parameter (soft patch - only update tickets, not statistics)
  useEffect(() => {
    if (!hasInitializedRef.current || priorities.length === 0) return undefined;

    // Initial mount uses the bootstrap effect; skip empty search until the user types
    if (skipInitialSearchLoadRef.current) {
      skipInitialSearchLoadRef.current = false;
      if (!searchTerm?.trim()) {
        return undefined;
      }
    }

    if (currentPage !== 0) {
      setCurrentPage(0);
      return undefined;
    }

    const searchTimeout = setTimeout(() => {
      loadTickets(true);
    }, searchTerm.trim() ? 500 : 300);

    return () => clearTimeout(searchTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (tickets.length > 0) {
      filterTickets();
    }
  }, [sortBy, tickets]);

  // Reload tickets when status, priority, or category filters change (not on initial mount)
  useEffect(() => {
    if (!hasInitializedRef.current || priorities.length === 0) return;

    if (skipInitialFilterLoadRef.current) {
      skipInitialFilterLoadRef.current = false;
      return;
    }

    if (currentPage !== 0) {
      setCurrentPage(0);
      return;
    }

    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, categoryFilter]);

  // SLA timers: seed from API remaining/overdue fields, tick every second (display countdown)
  useEffect(() => {
    if (ticketsForSla.length === 0 || statuses.length === 0) return;

    const buildTimers = (secondsSinceFetch) => {
      const nextTimers = new Map();
      ticketsForSla.forEach((ticket) => {
        if (!ticket.assignedTime) {
          return;
        }
        const timer = computeSlaTimerForTicket(ticket, secondsSinceFetch);
        if (timer) {
          nextTimers.set(ticket.id, timer);
        }
      });
      return nextTimers;
    };

    setSlaTimers(buildTimers(0));

    const hasLiveCountdownTicket = ticketsForSla.some((ticket) => {
      if (!ticket.assignedTime || isSlaClockFrozen(ticket, statuses)) {
        return false;
      }
      return computeSlaTimerForTicket(ticket, 0) !== null;
    });
    if (!hasLiveCountdownTicket) {
      return;
    }

    const interval = setInterval(() => {
      const tickSeconds = Math.floor(
        (Date.now() - ticketsSlaFetchedAtRef.current) / 1000
      );
      setSlaTimers(buildTimers(tickSeconds));
    }, 1000);

    return () => clearInterval(interval);
  }, [ticketsForSla, statuses]);


  const loadTickets = async (isSearchOperation = false, prioritiesOverride = null) => {
    try {
      // Use searchLoading for search operations to avoid full page reload
      if (isSearchOperation) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      setError('');

      let availablePriorities = prioritiesOverride ?? priorities;

      // Fetch tickets with server-side pagination and filters
      const params = { page: currentPage, size: pageSize };

      // Add search filter if provided
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add status filter if not 'all'
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add priority filter — Inquiry Priority Level id (BE OR-matches inquiry_priority_level_id)
      if (priorityFilter && priorityFilter !== 'all') {
        const parsed = parseInt(priorityFilter, 10);
        if (Number.isFinite(parsed)) {
          params.priorityId = parsed;
        }
      }

      // Inquiry Category: my-tickets BE has no inquiryCategoryId; filter client-side after fetch
      const response = await ticketService.getMyTickets(params);

      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      // data is PaginatedResponse with content, pageNumber, pageSize, totalElements, etc.
      const responseData = response?.data;

      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        const backendTickets = paginatedData.content || [];

        // Transform backend tickets to frontend format
        const transformedTickets = backendTickets.map(ticket => {
          // Prefer Inquiry Priority Level; fall back to legacy priority master
          let priority = 'medium';
          let priorityName = 'Medium';
          let priorityId = ticket.priorityId;
          const inquiryPriorityLabel = resolveTicketPriorityLabel(ticket);

          if (inquiryPriorityLabel !== '—') {
            priorityName = inquiryPriorityLabel;
            priority = inquiryPriorityLabel.toLowerCase();
          } else if (priorityId && availablePriorities.length > 0) {
            const priorityObj = availablePriorities.find(p => p.priorityId === priorityId || p.priorityId?.toString() === priorityId?.toString());
            if (priorityObj) {
              priority = (priorityObj.priorityCode || priorityObj.priorityName || 'medium').toLowerCase();
              priorityName = priorityObj.priorityName || priorityObj.priorityCode || 'Medium';
            }
          } else if (ticket.priority) {
            priority = ticket.priority.toLowerCase();
            priorityName = ticket.priority;
          }

          // SLA status reflects the CURRENT escalation level's window
          let slaStatus = 'on-track';
          if (ticket.slaRemainingMinutes !== null && ticket.slaRemainingMinutes !== undefined) {
            const current = computeSlaCountdown(
              {
                remainingMinutes: ticket.slaRemainingMinutes,
                remainingSeconds: ticket.slaRemainingSeconds,
                overdueMinutes: ticket.slaOverdueMinutes,
                overdueSeconds: ticket.slaOverdueSeconds,
              },
              0,
              { slaUsesBusinessHours: ticket.slaUsesBusinessHours === true }
            );
            if (current.overdueMinutes !== null) {
              slaStatus = 'breached';
            } else if (current.remainingMinutes * 60 + current.remainingSeconds < 3600) {
              slaStatus = 'at-risk';
            }
          }

          return {
            id: ticket.ticketId || ticket.ticketNumber,
            ticketId: ticket.ticketId,
            ticketNumber: ticket.ticketNumber,
            title: ticket.subject,
            description: ticket.description || '',
            status: ticket.status?.toLowerCase() || 'open',
            statusName: ticket.status,
            priority: priority,
            priorityName: priorityName,
            priorityId: priorityId,
            category: resolveTicketCategoryLabel(ticket),
            categoryId: ticket.categoryId,
            inquiryCategoryId: ticket.inquiryCategoryId,
            inquiryTypeName: ticket.inquiryTypeName,
            inquiryCategoryName: ticket.inquiryCategoryName,
            inquirySubCategoryName: ticket.inquirySubCategoryName,
            inquiryPriorityLevelName: ticket.inquiryPriorityLevelName,
            inquiryBreadcrumb: resolveInquiryBreadcrumb(ticket),
            customer: {
              name: ticket.requesterEmail ? ticket.requesterEmail.split('@')[0] : 'Unknown',
              email: ticket.requesterEmail || '',
              company: ''
            },
            assignedAgent: ticket.assigneeId ? `Agent ${ticket.assigneeId}` : 'Unassigned',
            assigneeId: ticket.assigneeId,
            assignedTime: ticket.assignedTime,
            inquiryTypeId: ticket.inquiryTypeId,
            inquirySubCategoryId: ticket.inquirySubCategoryId,
            inquiryPriorityLevelId: ticket.inquiryPriorityLevelId,
            slaApplicable: ticket.slaApplicable,
            slaDueDate: ticket.dueDate,
            createdAt: ticket.createdAt,
            updatedAt: ticket.updatedAt || ticket.createdAt,
            dueDate: ticket.resolvedAt || ticket.updatedAt,
            tags: [],
            attachments: ticket.attachmentCount || 0,
            comments: ticket.replyCount || 0,
            slaStatus: slaStatus,
            slaUsesBusinessHours: ticket.slaUsesBusinessHours === true,
            slaRemainingMinutes: ticket.slaRemainingMinutes,
            slaRemainingSeconds: ticket.slaRemainingSeconds,
            slaOverdueMinutes: ticket.slaOverdueMinutes,
            slaOverdueSeconds: ticket.slaOverdueSeconds,
            slaElapsedMinutes: ticket.slaElapsedMinutes,
            slaIsBreached: ticket.slaIsBreached,
            slaIsEscalated: ticket.slaIsEscalated,
            timeSpent: '0h 0m',
            estimatedTime: '0h',
            lastActivity: ticket.updatedAt || ticket.createdAt,
            countryId: ticket.countryId,
            countyId: ticket.countyId,
            subCountyId: ticket.subCountyId,
            wardId: ticket.wardId,
            countryName: ticket.countryName,
            countyName: ticket.countyName,
            subCountyName: ticket.subCountyName,
            wardName: ticket.wardName
          };
        });

        // Client-side Inquiry Category filter (my-tickets API has no inquiryCategoryId param)
        const categoryFiltered = categoryFilter && categoryFilter !== 'all'
          ? transformedTickets.filter((t) => String(t.inquiryCategoryId) === String(categoryFilter))
          : transformedTickets;

        const withResolvedStatus = categoryFiltered.map((t) =>
          enrichTicketStatusFromMaster(t, statuses)
        );

        ticketsSlaFetchedAtRef.current = Date.now();
        setTickets(withResolvedStatus);
        setFilteredTickets(withResolvedStatus);

        // Store pagination data
        setPaginationData({
          pageNumber: paginatedData.pageNumber || currentPage,
          pageSize: paginatedData.pageSize || pageSize,
          totalElements: paginatedData.totalElements || 0,
          totalPages: paginatedData.totalPages || 0,
          first: paginatedData.first !== undefined ? paginatedData.first : (currentPage === 0),
          last: paginatedData.last !== undefined ? paginatedData.last : false
        });

      } else {
        setError(responseData?.message || 'Failed to load tickets');
        toast.error(responseData?.message || 'Failed to load tickets');
        // Don't clear tickets during search - keep existing data visible
        if (!isSearchOperation) {
          setTickets([]);
          setFilteredTickets([]);
        }
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      const errorMsg = extractErrorMessage(err, 'Error loading tickets');
      setError(errorMsg);
      toast.error(errorMsg);
      // Don't clear tickets during search - keep existing data visible
      if (!isSearchOperation) {
        setTickets([]);
        setFilteredTickets([]);
      }
    } finally {
      // Reset appropriate loading state
      if (isSearchOperation) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  loadTicketsRef.current = loadTickets;
  loadStatisticsRef.current = loadStatistics;

  useEffect(() => {
    const onRealtimeRefresh = () => {
      loadStatisticsRef.current?.();
      loadTicketsRef.current?.(true);
    };
    window.addEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
    return () => window.removeEventListener('hd:queue-realtime-refresh', onRealtimeRefresh);
  }, []);

  const filterTickets = () => {
    let filtered = [...tickets];

    // Search, status, priority, and category filters are handled server-side
    // Only sorting is done client-side

    // Sort tickets
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'updated':
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        case 'priority':
          const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'due':
          return new Date(a.dueDate) - new Date(b.dueDate);
        default:
          return 0;
      }
    });

    setFilteredTickets(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'in-progress':
        return <Edit className="w-4 h-4 text-blue-500" />;
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'escalated':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Ticket className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'warning',
      'in-progress': 'default',
      resolved: 'success',
      escalated: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status.replace('-', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority, priorityName = null) => {
    const variants = {
      critical: 'destructive',
      high: 'destructive',
      medium: 'warning',
      low: 'success'
    };
    const displayText = priorityName || priority;
    return <Badge variant={variants[priority] || 'secondary'}>{displayText}</Badge>;
  };

  const getSLAStatusColor = (slaStatus) => {
    switch (slaStatus) {
      case 'on-track':
        return 'text-green-600';
      case 'at-risk':
        return 'text-yellow-600';
      case 'breached':
        return 'text-red-600';
      case 'completed':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900';
      case 'urgent': return 'text-red-50 dark:text-red-100 bg-red-800 dark:bg-red-950';
      case 'high': return 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  // Get light background color for ticket row based on priority
  const getPriorityBackgroundColor = (priority) => {
    // Return default background - no priority-based colors
    return 'bg-white dark:bg-gray-800';
  };

  // Get darker border color for ticket row based on priority
  const getPriorityBorderColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'border-l-4 border-green-400';
      case 'medium': return 'border-l-4 border-yellow-400';
      case 'high': return 'border-l-4 border-orange-400';
      case 'urgent': return 'border-l-4 border-red-700 dark:border-red-600';
      case 'critical': return 'border-l-4 border-red-400';
      default: return 'border-l-4 border-gray-300 dark:border-gray-600';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'open': return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900';
      case 'in-progress':
      case 'in_progress': return 'text-white dark:text-white bg-teal-600 dark:bg-teal-700';
      case 'resolved': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'closed': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900';
      case 'on-hold':
      case 'on_hold':
      case 'on hold': return 'text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900';
      case 'cancelled':
      case 'canceled': return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
      case 'reopen':
      case 're-open': return 'text-white dark:text-white bg-red-700 dark:bg-red-800';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getTimeRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;

    if (diff < 0) return 'Overdue';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Format time from minutes to readable string
  const formatTimeFromMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return 'N/A';
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 60) {
      return minutes < 0 ? `${absMinutes}m overdue` : `${absMinutes}m`;
    } else if (absMinutes < 1440) {
      const hours = Math.floor(absMinutes / 60);
      const mins = absMinutes % 60;
      return minutes < 0 ? `${hours}h ${mins}m overdue` : `${hours}h ${mins}m`;
    } else {
      const days = Math.floor(absMinutes / 1440);
      const hours = Math.floor((absMinutes % 1440) / 60);
      return minutes < 0 ? `${days}d ${hours}h overdue` : `${days}d ${hours}h`;
    }
  };

  const handleViewTicket = (ticketIdOrNumber) => {
    // Ensure we use numeric ticketId for navigation
    // ticketIdOrNumber could be numeric ticketId or ticket number string
    let numericTicketId = ticketIdOrNumber;

    // If it's a string that looks like a ticket number (e.g., "TKT-002997"), try to find the numeric ID
    if (typeof ticketIdOrNumber === 'string' && ticketIdOrNumber.includes('TKT-')) {
      const ticket = tickets.find(t => t.ticketNumber === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    } else {
      // Try to find ticket to get numeric ID if we have a numeric value
      const ticket = tickets.find(t => t.ticketId === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    }

    // Validate that we have a valid ID (numeric or valid string)
    if (!numericTicketId || (typeof numericTicketId === 'string' && numericTicketId.includes('TKT-'))) {
      toast.error('Invalid ticket ID. Please refresh and try again.');
      return;
    }

    const ticket = tickets.find(t => t.ticketId === numericTicketId || t.id === numericTicketId);
    toast.success(`Opening ticket ${ticket?.ticketNumber || numericTicketId} for editing...`);
    navigate(`/agent/tickets/${numericTicketId}`, { state: { mode: 'edit' } });
  };

  const resolveNumericTicketId = (ticketIdOrNumber) => {
    let numericTicketId = ticketIdOrNumber;

    if (typeof ticketIdOrNumber === 'string' && ticketIdOrNumber.includes('TKT-')) {
      const ticket = tickets.find(t => t.ticketNumber === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    } else {
      const ticket = tickets.find(t => t.ticketId === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    }

    if (!numericTicketId || (typeof numericTicketId === 'string' && numericTicketId.includes('TKT-'))) {
      return null;
    }
    return numericTicketId;
  };

  const ticketForManualMatrixEscalationCheck = (ticket) => (
    ticket
      ? {
        ...ticket,
        dueDate: ticket.slaDueDate,
        status: ticket.statusCode ?? ticket.status,
      }
      : null
  );

  const handleManualMatrixEscalateFromMyTickets = (ticketIdOrNumber) => {
    const numericTicketId = resolveNumericTicketId(ticketIdOrNumber);
    if (!numericTicketId) {
      toast.error('Invalid ticket ID. Please refresh and try again.');
      return;
    }

    const ticket = tickets.find(t => t.ticketId === numericTicketId || t.id === numericTicketId);
    if (!manualMatrixEscalationPerms.canView) {
      return;
    }
    if (!isManualMatrixEscalationEligible(ticketForManualMatrixEscalationCheck(ticket))) {
      toast.error('This ticket is not eligible for manual Matrix escalation.');
      return;
    }

    toast.success(`Opening ticket ${ticket?.ticketNumber || numericTicketId} to escalate...`);
    navigate(`/agent/tickets/${numericTicketId}`, {
      state: { mode: 'edit', openManualEscalate: true },
    });
  };

  const handleCommentTicket = (ticketIdOrNumber) => {
    // Ensure we use numeric ticketId for navigation
    // ticketIdOrNumber could be numeric ticketId or ticket number string
    let numericTicketId = ticketIdOrNumber;

    // If it's a string that looks like a ticket number (e.g., "TKT-002997"), try to find the numeric ID
    if (typeof ticketIdOrNumber === 'string' && ticketIdOrNumber.includes('TKT-')) {
      const ticket = tickets.find(t => t.ticketNumber === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    } else {
      // Try to find ticket to get numeric ID if we have a numeric value
      const ticket = tickets.find(t => t.ticketId === ticketIdOrNumber || t.id === ticketIdOrNumber);
      numericTicketId = ticket?.ticketId || ticketIdOrNumber;
    }

    // Validate that we have a valid ID (numeric or valid string)
    if (!numericTicketId || (typeof numericTicketId === 'string' && numericTicketId.includes('TKT-'))) {
      toast.error('Invalid ticket ID. Please refresh and try again.');
      return;
    }

    const ticket = tickets.find(t => t.ticketId === numericTicketId || t.id === numericTicketId);
    toast.success(`Opening ticket ${ticket?.ticketNumber || numericTicketId} in view mode...`);
    navigate(`/agent/tickets/${numericTicketId}`, { state: { mode: 'view', openComments: true, scrollToComments: true } });
  };

  const handleEscalateTicket = (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    const ticketStatus = ticket?.status?.toLowerCase();

    // Prevent escalation for closed or resolved tickets
    if (ticketStatus === 'closed' || ticketStatus === 'resolved') {
      toast.error(`Cannot escalate ticket ${ticketId}. Ticket is ${ticketStatus}.`);
      return;
    }

    if (ticketStatus === 'escalated') {
      toast.error(`Ticket ${ticketId} is already escalated`);
      return;
    }
    toast.warning(`Escalating ticket ${ticketId}...`);
    navigate('/agent/tickets/escalation', { state: { ticketId, ticket } });
  };

  const handleResolveTicket = (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (ticket?.status === 'resolved') {
      toast(`Ticket ${ticketId} is already resolved`, { icon: 'ℹ️' });
      return;
    }
    toast.success(`Opening resolution form for ${ticketId}...`);
    navigate('/agent/tickets/resolution', { state: { ticketId, ticket } });
  };

  const handleMoreAction = (action, ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);

    switch (action) {
      case 'archive':
        setTickets(prev => prev.map(t =>
          t.id === ticketId ? { ...t, archived: true, status: 'archived' } : t
        ));
        toast.success(`Ticket ${ticketId} archived successfully`);
        break;
      case 'star':
        setTickets(prev => prev.map(t =>
          t.id === ticketId ? { ...t, starred: !t.starred } : t
        ));
        const isStarred = ticket?.starred;
        toast.success(`Ticket ${ticketId} ${isStarred ? 'unstarred' : 'starred'}`);
        break;
      case 'assign':
        toast(`Opening reassignment dialog for ${ticketId}...`, { icon: '📋' });
        navigate('/agent/tickets/assignment', { state: { ticketId, ticket, isReassignment: true } });
        break;
      default:
        toast(`Action: ${action}`, { icon: 'ℹ️' });
    }
    setShowMoreMenu(null);
  };

  const handleRefresh = () => {
    loadStatistics();
    loadTickets();
  };

  const selectedDuplicateTicket = useMemo(() => {
    if (!selectedTicketId) return null;
    return filteredTickets.find(
      (t) => Number(t.ticketId || t.id) === Number(selectedTicketId),
    ) || null;
  }, [filteredTickets, selectedTicketId]);

  const canLinkSelectedAsDuplicate = useMemo(
    () => Boolean(
      selectedDuplicateTicket
      && !DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(getTicketStatusCode(selectedDuplicateTicket)),
    ),
    [selectedDuplicateTicket],
  );

  const exitDuplicateSelectMode = () => {
    setDuplicateSelectMode(false);
    setSelectedTicketId(null);
    setShowAttachDuplicateModal(false);
  };

  const handleLinkAsDuplicateAction = () => {
    if (!duplicateSelectMode) {
      setDuplicateSelectMode(true);
      setSelectedTicketId(null);
      return;
    }
    if (!selectedTicketId) {
      setDuplicateSelectMode(false);
      return;
    }
    if (!canLinkSelectedAsDuplicate) {
      toast.error('This ticket cannot be linked as a duplicate');
      return;
    }
    setShowAttachDuplicateModal(true);
  };

  const handleDuplicateLinked = () => {
    toast.success('Duplicate ticket linked successfully');
    exitDuplicateSelectMode();
    loadTickets();
  };

  const handleNewTicket = () => {
    toast.success('Opening new ticket form...');
    if (onCreateTicket) {
      onCreateTicket();
      return;
    }
    navigate('/agent/tickets/new');
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
    toast(showFilters ? 'Filters hidden' : 'Filters shown', { icon: 'ℹ️' });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
    toast.success('All filters cleared');
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const fetchMyTicketsForExport = async () => {
    const params = { fetchAll: true };
    if (searchTerm && searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (priorityFilter && priorityFilter !== 'all') {
      const parsed = parseInt(priorityFilter, 10);
      if (Number.isFinite(parsed)) params.priorityId = parsed;
    }
    const response = await ticketService.getMyTickets(params);
    const data = response?.data?.data;
    let content = Array.isArray(data) ? data : (data?.content || []);
    if (categoryFilter && categoryFilter !== 'all') {
      content = content.filter((t) => String(t.inquiryCategoryId) === String(categoryFilter));
    }
    return (content || []).map((ticket) => {
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        inquiryType: ticket.inquiryTypeName || '—',
        inquiryCategory: resolveTicketCategoryLabel(ticket),
        inquirySubCategory: ticket.inquirySubCategoryName || '—',
        inquiryPriority: resolveTicketPriorityLabel(ticket),
        category: resolveTicketCategoryLabel(ticket),
        status: (ticket.status || 'open').toLowerCase(),
        priorityName: resolveTicketPriorityLabel(ticket),
        createdAt: ticket.createdAt
      };
    });
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchMyTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Inquiry Type', 'Inquiry Category', 'Inquiry Sub Category', 'Inquiry Priority', 'Status', 'Created'];
      const csvRows = rows.map((r, i) => [
        i + 1, r.ticketNumber, r.subject, r.inquiryType, r.inquiryCategory, r.inquirySubCategory, r.inquiryPriority, r.status, formatDateTimeExport(r.createdAt)
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...csvRows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tickets exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchMyTicketsForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('My Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1, r.ticketNumber, (r.subject || '').substring(0, 30), (r.inquiryCategory || '').substring(0, 14), (r.inquiryPriority || '').substring(0, 12), r.status, formatDateTimeExport(r.createdAt)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket Number', 'Subject', 'Inquiry Category', 'Inquiry Priority', 'Status', 'Created']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`my-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Tickets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage your assigned tickets and track your progress
          </p>
        </div>
        <div className="flex gap-3 flex-nowrap">
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportCsv}
                  >
                    <FileText className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleToggleFilters}
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
          {canDuplicateLink && (
            <Button
              variant="outline"
              className={`flex items-center gap-2 ${
                duplicateSelectMode
                  ? 'bg-amber-400 border-amber-500 text-amber-950 hover:bg-amber-500 dark:bg-amber-500 dark:border-amber-400 dark:text-amber-950 dark:hover:bg-amber-400'
                  : 'border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 hover:bg-amber-50 dark:hover:bg-amber-900/30'
              }`}
              onClick={handleLinkAsDuplicateAction}
            >
              <Copy className="w-4 h-4" />
              Link as Duplicate
            </Button>
          )}
          <Button
            className="flex items-center gap-2"
            onClick={handleNewTicket}
          >
            <Ticket className="w-4 h-4" />
            New Ticket
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="w-4 h-4" />
          <div>
            <h4 className="font-semibold">Error loading tickets</h4>
            <p className="text-sm">{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadTickets}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading tickets...</p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-nowrap">
              {/* Search Input */}
              <div className="relative flex-1 min-w-0">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    // Prevent form submission on Enter key
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full"
                />
              </div>

              {/* Status Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Status</option>
                  {statuses.map(status => {
                    const statusCode = status.statusCode || status.statusName?.toLowerCase() || '';
                    return (
                      <option key={status.statusId || statusCode} value={statusCode}>
                        {status.statusName || status.statusCode}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Inquiry Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Inquiry Priorities</option>
                  {inquiryPriorityLevels.map((priority) => (
                    <option
                      key={priority.id || priority.inquiryPriorityLevelId}
                      value={String(priority.id || priority.inquiryPriorityLevelId)}
                    >
                      {priority.priorityLevelName || priority.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Inquiry Category Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="all">All Inquiry Categories</option>
                  {inquiryCategories.map((category) => {
                    const id = category.id || category.inquiryCategoryId;
                    return (
                      <option key={id} value={String(id)}>
                        {category.inquiryCategoryName || category.name || `Category ${id}`}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort By Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[160px]"
                >
                  <option value="updated">Last Updated</option>
                  <option value="created">Created Date</option>
                  <option value="priority">Priority</option>
                  <option value="due">Due Date</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Apply
                </Button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tickets List */}
      {!loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
          {/* Search Loading Indicator - Small progress bar at top of table */}
          {searchLoading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700 z-10">
              <div className="h-full bg-blue-600 animate-pulse" style={{ width: '30%' }}></div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {canDuplicateLink && duplicateSelectMode && (
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                      <span className="sr-only">Select</span>
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    SLA Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTickets.map((ticket, index) => {
                  // Calculate serial number based on current page
                  const serialNumber = currentPage * pageSize + index + 1;
                  const priority = ticket.priority || 'medium';
                  const bgColor = getPriorityBackgroundColor(priority);
                  const borderColor = getPriorityBorderColor(priority);

                  // Highlight open/in-progress only when CURRENT window timer is overdue
                  const isOpenOrInProgress = ticket.status === 'open' || ticket.status === 'in-progress';
                  const timer = slaTimers.get(ticket.id);
                  const isSlaTimedOut = timer?.isOverdue === true;
                  const shouldHighlight = isOpenOrInProgress && isSlaTimedOut;

                  const isReopened = ticket.status?.toLowerCase() === 'reopen';
                  const rowTicketId = ticket.ticketId || ticket.id;
                  return (
                    <tr
                      key={ticket.id}
                      className={`${isReopened ? 'bg-purple-50 dark:bg-purple-900/20' : shouldHighlight ? 'bg-red-50 dark:bg-red-900/20' : bgColor} ${borderColor} hover:shadow-md transition-all`}
                    >
                      {canDuplicateLink && duplicateSelectMode && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          <input
                            type="radio"
                            name="my-tickets-duplicate-select"
                            checked={Number(selectedTicketId) === Number(rowTicketId)}
                            onChange={() => setSelectedTicketId(Number(rowTicketId))}
                            disabled={DUPLICATE_LINK_BLOCKED_STATUS_CODES.has(getTicketStatusCode(ticket))}
                            aria-label={`Select ${ticket.ticketNumber || rowTicketId} for duplicate linking`}
                            className="h-4 w-4 border-gray-300 text-amber-600 focus:ring-amber-500 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {serialNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                              <Ticket className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {ticket.ticketNumber || ticket.id}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {ticket.title || ticket.subject}
                            </div>
                            {/* {(ticket.countryName || ticket.countyName || ticket.subCountyName || ticket.wardName) && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-xs">
                              📍 {[
                                ticket.countryName,
                                ticket.countyName,
                                ticket.subCountyName,
                                ticket.wardName
                              ].filter(Boolean).join(' > ')}
                            </div>
                          )} */}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          {isReopened && (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full text-purple-700 dark:text-purple-300 bg-purple-200 dark:bg-purple-800 border border-purple-300 dark:border-purple-600">
                              REOPENED
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {getStatusIcon(ticket.slaStatus || 'on-track')}
                          {/* <div className={`text-sm ml-2 ${getSLAStatusColor(ticket.slaStatus || 'on-track')}`}> */}
                          <div className={`text-sm ml-2 ${getSLAStatusColor(
                            slaTimers.get(ticket.id)?.isOverdue
                              ? 'breached'
                              : (ticket.slaStatus || 'on-track')
                          )
                            }`}>

                            {/* <div>{ticket.slaStatus ? ticket.slaStatus.replace('-', ' ') : 'On Track'}</div> */}

                            <div>
                              {(() => {
                                const timer = slaTimers.get(ticket.id);

                                if (timer?.isOverdue) {
                                  return 'breached';
                                }

                                return ticket.slaStatus
                                  ? ticket.slaStatus.replace('-', ' ')
                                  : 'On Track';
                              })()}
                            </div>

                            <div>
                              {(() => {
                                // Use countdown timer if available, otherwise fallback to static value
                                const timer = slaTimers.get(ticket.id);
                                if (timer !== undefined) {
                                  if (timer.isOverdue) {
                                    // formatTimeWithSeconds loses the "overdue" suffix at -0 minutes
                                    return timer.minutes === 0
                                      ? `${timer.seconds}s overdue`
                                      : formatTimeWithSeconds(-timer.minutes, -timer.seconds);
                                  }
                                  return formatTimeWithSeconds(timer.minutes, timer.seconds);
                                }
                                return 'SLA Not Assigned';
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(ticket.createdAt)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatTime(ticket.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={() => handleViewTicket(ticket.ticketId || ticket.id)}
                          >
                            <Pen className="w-4 h-4" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex items-center gap-2"
                            onClick={() => handleCommentTicket(ticket.ticketId || ticket.id)}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                          {manualMatrixEscalationPerms.canView
                            && isManualMatrixEscalationEligible(
                              ticketForManualMatrixEscalationCheck(ticket),
                            ) && (
                            <Button
                              size="sm"
                              className="flex items-center gap-2 bg-amber-600 text-white hover:bg-amber-700 border-0"
                              onClick={() => handleManualMatrixEscalateFromMyTickets(ticket.ticketId || ticket.id)}
                            >
                              <ArrowUp className="w-4 h-4" />
                              Escalate
                            </Button>
                          )}
                          {/* <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex items-center gap-2"
                          onClick={() => handleResolveTicket(ticket.id)}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Resolve
                        </Button> */}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {paginationData && paginationData.totalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{currentPage * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min((currentPage + 1) * pageSize, paginationData.totalElements)}</span> of{' '}
                    <span className="font-medium">{paginationData.totalElements}</span> results
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                    disabled={currentPage === 0 || paginationData.first}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, paginationData.totalPages) }, (_, i) => {
                    let pageNum;
                    if (paginationData.totalPages <= 5) {
                      pageNum = i;
                    } else if (currentPage < 3) {
                      pageNum = i;
                    } else if (currentPage > paginationData.totalPages - 4) {
                      pageNum = paginationData.totalPages - 5 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${pageNum === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationData.totalPages - 1))}
                    disabled={currentPage >= paginationData.totalPages - 1 || paginationData.last}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredTickets.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Ticket className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No tickets found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all'
                ? 'Try adjusting your search criteria'
                : 'You have no assigned tickets at the moment'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && priorityFilter === 'all' && (
              <Button onClick={handleNewTicket}>
                <Ticket className="w-4 h-4 mr-2" />
                Request Assignment
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {canDuplicateLink && selectedDuplicateTicket && (
        <AttachDuplicateModal
          currentTicketId={selectedDuplicateTicket.ticketId || selectedDuplicateTicket.id}
          currentTicketNumber={selectedDuplicateTicket.ticketNumber}
          linkMode="pick-canonical"
          isOpen={showAttachDuplicateModal}
          onClose={exitDuplicateSelectMode}
          onLinked={handleDuplicateLinked}
        />
      )}
    </div>
  );
};

export default MyTickets;
